import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { IconButtonComponent, InputComponent, SvgIconComponent } from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';
import { PageToolbarAction } from './page-toolbar.model';

type BackLink = string | unknown[];

@Component({
  standalone: true,
  selector: 'mon-page-toolbar',
  templateUrl: './page-toolbar.component.html',
  styleUrls: ['./page-toolbar.component.scss'],
  imports: [
    IconButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    RouterLink,
    SvgIconComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageToolbarComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly closeFiltersOnOutsidePointerDown = (event: PointerEvent): void => {
    if (!this.isFiltersOpen()) return;

    const target = this.elementFromTarget(event.target);

    if (this.isNotCloseClick(target)) return;

    this.isFiltersOpen.set(false);
  };

  @ViewChild('filtersPanel')
  private readonly filterPanel?: ElementRef<HTMLElement>;

  @ViewChild('filtersPanelBtn')
  private readonly filtersPanelBtn?: ElementRef<HTMLElement>;

  @Input()
  public titleTranslateKey = '';

  @Input()
  public selectedFiltersCount = 0;

  @Input()
  public backLink: BackLink | null = null;

  @Input()
  public searchControl: FormControl<string> | null = null;

  @Input()
  public searchDisabled = false;

  @Input()
  public searchPlaceholderTranslateKey = 'monitoring.search';

  @Input()
  public actions: PageToolbarAction[] = [];

  @Input()
  public showFiltersButton = true;

  @Output()
  public readonly actionClick = new EventEmitter<string>();

  protected readonly isFiltersOpen = signal(false);

  public ngOnInit(): void {
    this.document.addEventListener('pointerdown', this.closeFiltersOnOutsidePointerDown, true);
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('pointerdown', this.closeFiltersOnOutsidePointerDown, true);
    });
  }

  protected toggleFilters(event: MouseEvent): void {
    event.stopPropagation();

    if (!this.showFiltersButton) return;

    this.isFiltersOpen.update((isOpen) => !isOpen);
  }

  protected emitAction(action: PageToolbarAction): void {
    if (action.disabled) return;

    this.actionClick.emit(action.id);
  }

  private isNotCloseClick(target: Element | null): boolean {
    return (
      target !== null &&
      !!(
        this.filterPanel?.nativeElement?.contains(target) ||
        this.filtersPanelBtn?.nativeElement?.contains(target) ||
        this.elementFromTarget(target)?.closest('.cdk-overlay-container')
      )
    );
  }

  private elementFromTarget(target: EventTarget | null): Element | null {
    return target instanceof Element ? target : null;
  }
}
